// src/components/common/CloseButton.tsx
import React from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLang } from '../../context/LangContext';
import { ADMIN_LOGIN_PATH } from '../../constants';

/** Стартовая страница приложения — используется, если предыдущей страницы нет. */
const START_PAGE = '/';

/** На этих страницах кнопка «Закрыть» не показывается. */
const HIDDEN_ON = ['/', '/login', ADMIN_LOGIN_PATH];

/** Для этих страниц «Закрыть» ведёт не назад, а на фиксированный адрес. */
const FIXED_TARGETS: Record<string, string> = { '/results-gates': '/tests' };

const Wrapper = styled.div`
  position: fixed;
  top: 16px;
  left: 20px;
  z-index: 900;

  @media print {
    display: none !important;
  }
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1.5px solid ${({ theme }) => theme.colors.border};
  background: rgba(255, 255, 255, 0.15);
  color: ${({ theme }) => theme.colors.textLight};
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  transition: all 0.15s ease;
  backdrop-filter: blur(4px);

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Icon = styled.span`
  font-size: 0.9rem;
  line-height: 1;
`;

/**
 * react-router v6 хранит номер текущей записи истории в window.history.state.idx.
 * idx > 0 — значит внутри приложения есть предыдущая страница, куда можно вернуться.
 */
const hasPreviousPage = (): boolean => {
  const idx = (window.history.state as { idx?: number | null } | null)?.idx;
  return typeof idx === 'number' && idx > 0;
};

export const CloseButton: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLang();

  // Завершающий слэш отбрасываем, чтобы страница входа тоже распознавалась.
  const path = location.pathname.replace(/\/+$/, '') || '/';

  if (HIDDEN_ON.includes(path)) {
    return null;
  }

  const handleClose = () => {
    const fixedTarget = FIXED_TARGETS[path];
    if (fixedTarget) {
      navigate(fixedTarget);
      return;
    }
    if (hasPreviousPage()) {
      navigate(-1);
      return;
    }
    navigate(START_PAGE, { replace: true });
  };

  return (
    <Wrapper>
      <CloseBtn type="button" onClick={handleClose} title={t('closePage')} aria-label={t('closePage')}>
        <Icon aria-hidden="true">&times;</Icon>
      </CloseBtn>
    </Wrapper>
  );
};

export default CloseButton;